import { supabase } from './supabaseClient';

// Utility: estimate reading time from markdown length
export function estimateReadingTime(text = '') {
  const words = (text || '').split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

export async function fetchPosts() {
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .order('date_published', { ascending: false });
    if (error) throw error;
    const posts = (data || []).map(mapRowToPost);
    return posts;
  } catch (e) {
    console.warn('blogService.fetchPosts fallback to mockPosts due to:', e?.message || e);
    const { mockPosts } = await import('../data/mockPosts');
    return mockPosts.map((p) => ({
      id: p.id,
      title: p.title,
      slug: (p.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      excerpt: p.excerpt,
      content_md: typeof p.content === 'string' ? p.content : '',
      image_url: p.imageUrl,
      date_published: p.date,
      author: p.author || 'Ravana Team',
      category: p.category || 'general',
      tags: p.tags || [],
    }));
  }
}

export async function fetchPostByIdOrSlug(idOrSlug) {
  // Try Supabase by slug, then id
  try {
    let query = supabase.from('blog_posts').select('*');
    if (isNaN(Number(idOrSlug))) {
      query = query.eq('slug', idOrSlug).single();
    } else {
      query = query.eq('id', Number(idOrSlug)).single();
    }
    const { data, error } = await query;
    if (error) throw error;
    return mapRowToPost(data);
  } catch (e) {
    const { mockPosts } = await import('../data/mockPosts');
    const byId = mockPosts.find((p) => String(p.id) === String(idOrSlug));
    const bySlug = mockPosts.find((p) => (p.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === idOrSlug);
    const p = byId || bySlug;
    if (!p) return null;
    return {
      id: p.id,
      title: p.title,
      slug: (p.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      excerpt: p.excerpt,
      content_md: typeof p.content === 'string' ? p.content : '',
      image_url: p.imageUrl,
      date_published: p.date,
      author: p.author || 'Ravana Team',
      category: p.category || 'general',
      tags: p.tags || [],
    };
  }
}

export async function createTopic(topic) {
  // Use Edge Function with service role to bypass client 401
  const { data, error } = await supabase.functions.invoke('blog-writer', {
    body: { action: 'create_topic', ...topic },
  });
  if (error) throw error;
  return data?.topic;
}

export async function listTopics() {
  const { data, error } = await supabase
    .from('blog_topics')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function triggerGeneration({ topicIds = [], count = 2 } = {}) {
  // Call Supabase Edge Function to generate blogs
  const { data, error } = await supabase.functions.invoke('blog-writer', {
    body: { action: 'generate', topic_ids: topicIds, count },
  });
  if (error) throw error;
  return data;
}

function mapRowToPost(row) {
  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt,
    content_md: row.content_md,
    image_url: row.image_url,
    date_published: row.date_published,
    author: row.author || 'Ravana Team',
    category: row.category || 'general',
    tags: Array.isArray(row.tags) ? row.tags : [],
    seo_score: row.seo_score || 0,
    likes: row.likes || 0,
    views: row.views || 0,
    faqs: row.faqs || []
  };
}

export async function fetchComments(postId) {
  const { data, error } = await supabase
    .from('blog_comments')
    .select('*')
    .eq('post_id', postId)
    .eq('is_approved', true)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function createComment(comment) {
  const { data, error } = await supabase
    .from('blog_comments')
    .insert([{
      ...comment,
      is_approved: false
    }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getBlogStats() {
  const { count: postCount } = await supabase.from('blog_posts').select('*', { count: 'exact', head: true });
  const { count: topicCount } = await supabase.from('blog_topics').select('*', { count: 'exact', head: true }).eq('is_active', true);

  const { data: posts } = await supabase.from('blog_posts').select('seo_score');
  const avgSeo = posts?.length ? posts.reduce((acc, p) => acc + (p.seo_score || 0), 0) / posts.length : 0;

  return {
    totalPosts: postCount || 0,
    activeTopics: topicCount || 0,
    seoHealth: Math.round(avgSeo || 0)
  };
}

export async function updateSchedule(config) {
  const { data, error } = await supabase
    .from('blog_schedule')
    .upsert({
      id: 1,
      ...config,
      updated_at: new Date()
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function incrementLikes(postId) {
  // Optimization: Use RPC if available, else manual update
  const { data } = await supabase.from('blog_posts').select('likes').eq('id', postId).single();
  const newLikes = (data?.likes || 0) + 1;
  const { error } = await supabase.from('blog_posts').update({ likes: newLikes }).eq('id', postId);
  if (error) throw error;
  return newLikes;
}
